import { action, computed, observable } from 'mobx';
import { Duration } from 'luxon';
import {
  CatalogEntry,
  LearningPath,
  Modality,
  VocabularyItem,
  VocabularyNamespaces,
} from 'types';
import { EnablementFeatureFlags } from 'config/featureFlags';
import CatalogStore from 'stores/Catalog';
import type FeaturesStore from 'stores/Features';
import UIStore from 'stores/UI';
import VocabularyStore from 'stores/Vocabulary';
import UserStore from 'stores/User';
import {
  getLearningPath,
  getCustomSkillPath,
  joinLearningPath,
  unjoinLearningPath,
} from 'services/LearningPathService';
import type LearningPathsStore from 'stores/LearningPaths';

/** When catalog has no duration fields for an exam, assume 3 hours for path totals. */
const EXAM_DEFAULT_DURATION_MINUTES = 180;

/**
 * Minutes for estimated duration aggregation. Prefers total_duration; then duration + unit;
 * exams with no metadata fall back to {@link EXAM_DEFAULT_DURATION_MINUTES}.
 */
export function getOfferingDurationMinutes(entry: CatalogEntry): number {
  const fromTotal = [entry.total_duration, entry.metadata?.total_duration].find(
    (v): v is number => typeof v === 'number' && v > 0,
  );
  if (fromTotal != null) {
    return fromTotal;
  }

  const raw = entry.duration;
  if (typeof raw === 'number' && raw > 0) {
    const unit =
      entry.duration_unit ??
      entry.metadata?.duration_unit ??
      ('minutes' as const);

    return Math.round(Duration.fromObject({ [unit]: raw }).as('minutes'));
  }

  if (entry.modality === Modality.Exam) {
    return EXAM_DEFAULT_DURATION_MINUTES;
  }

  return 0;
}

/** Same resolution order as OfferingsTab `getCatalogEntry`. */
export function resolveCatalogEntry(
  catalogStore: CatalogStore,
  code: string | undefined,
): CatalogEntry | undefined {
  if (!code) return undefined;
  const lower = code.toLowerCase();
  return (
    catalogStore.entriesByCode?.[lower] ??
    catalogStore.uniqueCatalogEntries?.[code] ??
    catalogStore.allUniqueCatalogEntries?.[code]
  );
}

export type AggregatedMetadata = {
  totalDuration: number;
  /** Per lp_element duration in minutes (aligned with `lp_elements` index); null when no catalog entry. */
  lpElementDurations: (number | null)[];
  totalGuidedExercises: number;
  totalQuiz: number;
  totalVideos: number;
  productLabels: string[];
  audienceLabels: string[];
  topicLabels: string[];
};

class SkillsPathLandingStore {
  @observable isLoading = true;

  @observable isDataLoaded = false;

  @observable code: string = null;

  @observable learningPath: LearningPath = null;

  @observable isError = false;

  /** HTTP status from the failed request (e.g. 403 for forbidden) */
  @observable errorStatus: number | null = null;

  @observable productVocabulary: VocabularyItem[] = null;

  @observable audienceVocabulary: VocabularyItem[] = null;

  @observable topicVocabulary: VocabularyItem[] = null;

  @observable isEnrolling = false;

  /** Explicit joined state; kept in sync with learningPath.joined for reliable UI updates */
  @observable isJoined = false;

  uiStore: UIStore;

  catalogStore: CatalogStore;

  vocabularyStore: VocabularyStore;

  userStore: UserStore;

  learningPathStore: LearningPathsStore;

  featuresStore: FeaturesStore;

  constructor(
    uiStore: UIStore,
    catalogStore: CatalogStore,
    vocabularyStore: VocabularyStore,
    userStore: UserStore,
    learningPathStore: LearningPathsStore,
    featuresStore: FeaturesStore,
  ) {
    this.uiStore = uiStore;
    this.catalogStore = catalogStore;
    this.vocabularyStore = vocabularyStore;
    this.userStore = userStore;
    this.learningPathStore = learningPathStore;
    this.featuresStore = featuresStore;
  }

  /** True when enablements are still loading or path data is loading. */
  @computed get isPageLoading(): boolean {
    return !this.featuresStore.allFeaturesFetched || this.isLoading;
  }

  /** True when enablements are loaded and sidebar data is ready. */
  @computed get isSidebarLoaded(): boolean {
    return this.featuresStore.allFeaturesFetched && this.isSidebarReady;
  }

  /** True when enablements are loaded and skills paths feature is disabled for the user. */
  @computed get showForbidden(): boolean {
    return (
      this.featuresStore.allFeaturesFetched &&
      !this.featuresStore.isFeatureEnabled(
        'enablements',
        EnablementFeatureFlags.SkillsPathsEnabled,
        false,
      ) &&
      !this.userStore.isOrganizationLeader
    );
  }

  @computed get title() {
    if (!this.learningPath) return null;
    const { path_type: pathType, title: pathTitle } = this.learningPath;
    return pathType === 'custom' ? `Custom ${pathTitle}` : pathTitle;
  }

  @computed get isCustomSkillPath() {
    return this.learningPath?.path_type === 'custom';
  }

  @computed get isCurrentUserAuthor() {
    if (!this.learningPath?.author || !this.userStore.username) return false;
    return (
      this.learningPath.author.toLowerCase().trim() ===
      this.userStore.username.toLowerCase().trim()
    );
  }

  @computed get showCustomizeButton() {
    return this.isCustomSkillPath && this.isCurrentUserAuthor;
  }

  @computed get showDeleteButton() {
    return this.isCustomSkillPath && this.isCurrentUserAuthor;
  }

  /**
   * Aggregates metadata from all offerings in the skills path.
   * Union of taxonomies, sum of durations/exercises/quizzes.
   */
  @computed get aggregatedMetadata(): AggregatedMetadata | null {
    if (!this.learningPath?.lp_elements?.length) {
      return {
        totalDuration: 0,
        lpElementDurations: [],
        totalGuidedExercises: 0,
        totalQuiz: 0,
        totalVideos: 0,
        productLabels: [],
        audienceLabels: [],
        topicLabels: [],
      };
    }

    const productTokens = new Set<string>();
    const audienceTokens = new Set<string>();
    const topicTokens = new Set<string>();

    let totalDuration = 0;
    const lpElementDurations: (number | null)[] = [];
    let totalGuidedExercises = 0;
    let totalQuiz = 0;
    let totalVideos = 0;

    this.learningPath.lp_elements.forEach((element) => {
      if (!element?.code) {
        lpElementDurations.push(null);
        return;
      }

      const entry = resolveCatalogEntry(this.catalogStore, element.code);
      if (!entry) {
        lpElementDurations.push(null);
        return;
      }

      const duration = getOfferingDurationMinutes(entry);

      totalDuration += duration;
      lpElementDurations.push(duration);
      totalGuidedExercises += entry.total_ge ?? entry.metadata?.total_ge ?? 0;
      totalQuiz += entry.metadata?.total_quiz ?? 0;

      if (
        entry.metadata?.total_video_duration &&
        entry.metadata.total_video_duration > 0
      ) {
        totalVideos += 1;
      }

      entry.metadata?.taxonomies?.products?.forEach((p) =>
        productTokens.add(p),
      );
      entry.metadata?.taxonomies?.audiences?.forEach((a) =>
        audienceTokens.add(a),
      );
      entry.metadata?.taxonomies?.topics?.forEach((t) => topicTokens.add(t));

      entry.taxonomies?.products?.forEach((p) => productTokens.add(p));
      entry.taxonomies?.audiences?.forEach((a) => audienceTokens.add(a));
      entry.taxonomies?.topics?.forEach((t) => topicTokens.add(t));
    });

    const productVocabulary = this.productVocabulary || [];
    const audienceVocabulary = this.audienceVocabulary || [];
    const topicVocabulary = this.topicVocabulary || [];

    const productLabels = Array.from(productTokens)
      .map(
        (token) =>
          productVocabulary?.find((v) => v.token === token)?.display_name,
      )
      .filter(Boolean);
    const audienceLabels = Array.from(audienceTokens)
      .map(
        (token) =>
          audienceVocabulary?.find((v) => v.token === token)?.display_name,
      )
      .filter(Boolean);
    const topicLabels = Array.from(topicTokens)
      .map(
        (token) =>
          topicVocabulary?.find((v) => v.token === token)?.display_name,
      )
      .filter(Boolean);

    return {
      totalDuration,
      lpElementDurations,
      totalGuidedExercises,
      totalQuiz,
      totalVideos,
      productLabels,
      audienceLabels,
      topicLabels,
    };
  }

  @computed get language() {
    return this.uiStore.currentLanguage || 'en-US';
  }

  /**
   * True when sidebar can show real data: we have learningPath and either
   * no lp_elements, catalog is loaded, or at least one lp_element resolved.
   */
  @computed get isSidebarReady(): boolean {
    if (!this.learningPath) return false;
    const lpElements = this.learningPath.lp_elements ?? [];
    if (lpElements.length === 0) return true;
    if (this.catalogStore.loaded) return true;
    return lpElements.some(
      (el) => el?.code && resolveCatalogEntry(this.catalogStore, el.code),
    );
  }

  @action fetchSkillsPathData = async (code: string) => {
    try {
      this.isLoading = true;
      this.isError = false;
      this.errorStatus = null;
      this.code = code;

      let data: LearningPath;
      try {
        data = await getLearningPath(code);
      } catch (learningPathError: any) {
        if (learningPathError?.response?.status === 404) {
          try {
            data = await getCustomSkillPath(code);
          } catch (customError) {
            throw customError;
          }
        } else {
          throw learningPathError;
        }
      }

      this.learningPath = data;
      this.isJoined = data.joined ?? false;

      // Add to learningPathStore so join/unjoin can mutate the same object (like LearningPathCard)
      const existingIndex = this.learningPathStore.learningPaths.findIndex(
        (p) => p.code === data?.code,
      );
      if (existingIndex >= 0) {
        this.learningPathStore.learningPaths[existingIndex] = data;
      } else {
        this.learningPathStore.learningPaths.push(data);
      }
      this.learningPathStore.filterEntries();

      const [productVocabulary, audienceVocabulary, topicVocabulary] =
        await Promise.all([
          this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyProducts,
            this.language,
          ),
          this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyAudiences,
            this.language,
          ),
          this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyTopics,
            this.language,
          ),
        ]);

      if (productVocabulary?.length) this.productVocabulary = productVocabulary;
      if (audienceVocabulary?.length)
        this.audienceVocabulary = audienceVocabulary;
      if (topicVocabulary?.length) this.topicVocabulary = topicVocabulary;

      this.isDataLoaded = true;
    } catch (error: any) {
      this.isError = true;
      this.errorStatus = error?.response?.status ?? null;
      this.isDataLoaded = false;
    } finally {
      this.isLoading = false;
    }
  };

  @action joinLearningPath = async (code: string): Promise<void> => {
    if (this.isEnrolling || !this.learningPath) return;
    this.isEnrolling = true;
    try {
      await joinLearningPath(code);
      this.learningPath.joined = true;
      this.isJoined = true;
      // Sync path in learningPathStore (may be a different object if init replaced the array)
      const pathInStore = this.learningPathStore.learningPaths.find(
        (p) => p.code === code,
      );
      if (pathInStore) pathInStore.joined = true;
      this.learningPathStore.filterEntries();
    } finally {
      this.isEnrolling = false;
    }
  };

  @action unjoinLearningPath = async (code: string): Promise<void> => {
    if (this.isEnrolling || !this.learningPath) return;
    this.isEnrolling = true;
    try {
      await unjoinLearningPath(code);
      this.learningPath.joined = false;
      this.isJoined = false;
      // Sync path in learningPathStore (may be a different object if init replaced the array)
      const pathInStore = this.learningPathStore.learningPaths.find(
        (p) => p.code === code,
      );
      if (pathInStore) pathInStore.joined = false;
      this.learningPathStore.filterEntries();
    } finally {
      this.isEnrolling = false;
    }
  };
}

export default SkillsPathLandingStore;
